import { xor } from "lodash-es";
import { useState } from "react";
import type { FleetConfig, FleetConfigItem } from "src/types";

const INITIAL_FILTERS = {
  category: [],
  euro: [],
  fuel: [],
};
export type OrderColumn = "category" | "euro" | "fuel" | "allowed";
export type FilterColumn = "category" | "euro" | "fuel";
export type OrderDirection = "asc" | "desc";
export type Filters = Record<FilterColumn, string[]>;

export function useFleetTable() {
  const [orderBy, setOrderBy] = useState<OrderColumn>("category");
  const [orderDirection, setOrderDirection] = useState<OrderDirection>("asc");
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);

  function handleSort(column: OrderColumn) {
    if (column !== orderBy) {
      setOrderBy(column);
      setOrderDirection("asc");
    }
    if (column === orderBy) {
      toggleOrderDirection();
    }
  }

  function toggleOrderDirection() {
    if (orderDirection === "asc") {
      setOrderDirection("desc");
    } else if (orderDirection === "desc") {
      setOrderDirection("asc");
    }
  }

  function updateFilters(filterKey: FilterColumn, updatedFilters: Array<string | number>) {
    setFilters({
      ...filters,
      [filterKey]: xor(filters[filterKey], updatedFilters),
    });
  }

  return {
    orderBy,
    orderDirection,
    filters,
    setOrderBy,
    setOrderDirection,
    setFilters,
    handleSort,
    toggleOrderDirection,
    updateFilters,
  };
}

export function useFilteredAndSortedFleetConfig(
  fleetConfig: FleetConfig,
  filters: Filters,
  orderBy: OrderColumn,
  orderDirection: OrderDirection
) {
  function filterData(data: FleetConfig, filters: Filters): FleetConfig {
    return data.filter((item) => {
      const categoryMatch = filters.category.includes(item.category) || filters.category.length === 0;
      const euroMatch = filters.euro.includes(item.euroTranslation) || filters.euro.length === 0;
      const fuelMatch = filters.fuel.includes(item.fuel) || filters.fuel.length === 0;

      return categoryMatch && euroMatch && fuelMatch;
    });
  }

  function sortData(data: FleetConfig, column: OrderColumn, direction: OrderDirection) {
    switch (column) {
      case "allowed":
        return sortAllowed(data, direction);
      default:
        return defaultSort(data, column, direction);
    }
  }

  function sortAllowed(data: FleetConfig, direction: OrderDirection) {
    const sortedByAllowance = defaultSort(data, "allowed", direction);
    const sortedByDisabled = defaultSort(sortedByAllowance, "disabled", direction);
    return sortedByDisabled;
  }

  function defaultSort(data: FleetConfig, column: OrderColumn | keyof FleetConfigItem, direction: OrderDirection) {
    return [...data].sort((a, b) => {
      const nameA = a[column].toString().toUpperCase(); // ignore upper and lowercase
      const nameB = b[column].toString().toUpperCase(); // ignore upper and lowercase
      if (direction === "asc") {
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
      }
      if (direction === "desc") {
        if (nameA > nameB) return -1;
        if (nameA < nameB) return 1;
      }

      // names must be equal
      return 0;
    });
  }
  const filteredData = filterData(fleetConfig, filters);
  const sortedData = sortData(filteredData, orderBy, orderDirection);
  return sortedData;
}
